import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import { Button, FilterPill, Input, Pagination, RegionState } from '@freelancelabs/components';
import { MagnifyingGlass } from '@freelancelabs/icons';

import ListCard from '../../components/ListCard';
import { AccessibleAccountsDocument, ActivityLogDocument } from '../../graphql/__generated__/types';
import JournalTable from './JournalTable';
import { CURATED_ACTION_CODES, actionLabel } from './actionCatalog';
import { journalRefusalOf } from './journalRefusal';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Les préréglages de période. La borne basse seule : un journal se lit « depuis quand »,
 * la borne haute est toujours maintenant. « Depuis le début » ne contraint rien et part
 * en `null`, donc pas de pastille sur ce choix (il ne retranche rien).
 */
type Period = 'ALL' | 'P7D' | 'P30D' | 'P3M' | 'P12M';

const periodFrom = (period: Period): string | null => {
    if (period === 'ALL') {
        return null;
    }
    const date = new Date();
    switch (period) {
        case 'P7D':
            date.setDate(date.getDate() - 7);
            break;
        case 'P30D':
            date.setDate(date.getDate() - 30);
            break;
        case 'P3M':
            date.setMonth(date.getMonth() - 3);
            break;
        case 'P12M':
            date.setFullYear(date.getFullYear() - 1);
            break;
    }
    return date.toISOString();
};

/**
 * La page « Journal d'activité » du back-office (PCG-337) : le journal d'audit des
 * gestes d'administration, en LECTURE SEULE. Barre de filtres, table dans une carte
 * bordée (pas d'accordéon), ligne dépliable pour le détail via l'icône œil.
 *
 * **Le référentiel des codes est côté front** (`actionCatalog.ts`), seule source de la
 * pastille et du menu « Type d'action ». Quand aucun type n'est coché, l'écran envoie la
 * LISTE CURÉE des codes plutôt qu'un tableau vide : `actions` vide veut dire « toutes »
 * pour l'API, ce qui ferait entrer l'émission de jeton Metabase dans le compteur. La
 * liste curée l'exclut, donc « N événements » ne compte que des gestes d'administration.
 *
 * **Pas de filtre par acteur**, décision de l'API reprise à l'écran : chercher « qui a
 * agi » surveillerait des collègues au lieu de tracer ce qui est arrivé à un compte.
 */
const JournalPage = () => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedActions, setSelectedActions] = useState<string[]>([]);
    const [organizationId, setOrganizationId] = useState<string>('');
    const [period, setPeriod] = useState<Period>('ALL');
    const [page, setPage] = useState(1);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    // La borne basse ne se recalcule qu'au changement de préréglage : recalculée à
    // chaque rendu, son horodatage bougerait et relancerait la requête en boucle.
    const from = useMemo(() => periodFrom(period), [period]);

    const actionsKey = selectedActions.join(',');
    // Tout changement de critère renvoie en première page : rester en page 3 d'un
    // résultat qui n'en a plus qu'une montre un tableau vide et se lit comme un bug.
    useEffect(() => setPage(1), [debouncedSearch, actionsKey, organizationId, period]);

    // La liste des comptes pour le filtre « Société ». `accessibleAccounts` est la
    // requête faite pour les sélecteurs (le périmètre entier d'un coup), la même que la
    // fiche de compte emploie faute d'une lecture d'un compte seul.
    const { data: accountsData } = useQuery(AccessibleAccountsDocument);
    const accounts = accountsData?.accessibleAccounts ?? [];

    const { data, loading, error } = useQuery(ActivityLogDocument, {
        variables: {
            // Aucun type coché : la liste curée, jamais un tableau vide (« toutes »).
            actions: selectedActions.length > 0 ? selectedActions : CURATED_ACTION_CODES,
            organizationId: organizationId || null,
            from,
            to: null,
            search: debouncedSearch || null,
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
        },
        fetchPolicy: 'cache-and-network',
    });

    const result = data?.activityLog;
    const items = result?.items ?? [];
    // Jamais `items.length` : ça compterait une page. Le total de l'API suit les filtres.
    const total = result?.total ?? null;

    const filtered =
        Boolean(debouncedSearch) || selectedActions.length > 0 || organizationId !== '' || period !== 'ALL';
    const showSkeleton = loading && !data;
    const refusal = error ? journalRefusalOf(error) : null;

    const rangeStart = (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = (page - 1) * PAGE_SIZE + items.length;
    const summary =
        total === null
            ? c('Activity log').t`${rangeStart}–${rangeEnd}`
            : c('Activity log').ngettext(
                  msgid`${rangeStart}–${rangeEnd} of ${total} event`,
                  `${rangeStart}–${rangeEnd} of ${total} events`,
                  total
              );

    const actionOptions = useMemo(
        () =>
            CURATED_ACTION_CODES.map((code) => ({
                value: code,
                label: actionLabel(code),
                // Cherchable par libellé ET par code brut : un opérateur qui connaît
                // `membership.role_changed` le trouve sans deviner sa traduction.
                searchText: `${actionLabel(code)} ${code}`,
            })),
        []
    );

    const accountOptions = useMemo(
        () => [
            { value: '', label: c('Activity log filter').t`All accounts` },
            // Un compte sans identifiant ne peut pas filtrer : l'API veut un `ID`. Le
            // nom manquant retombe sur l'identifiant plutôt que sur un libellé vide.
            ...accounts
                .filter((account): account is typeof account & { id: string } => Boolean(account.id))
                .map((account) => ({ value: account.id, label: account.name ?? account.id })),
        ],
        [accounts]
    );

    const periodOptions: { value: Period; label: string }[] = [
        { value: 'ALL', label: c('Activity log filter').t`Since the beginning` },
        { value: 'P7D', label: c('Activity log filter').t`Last 7 days` },
        { value: 'P30D', label: c('Activity log filter').t`Last 30 days` },
        { value: 'P3M', label: c('Activity log filter').t`Last 3 months` },
        { value: 'P12M', label: c('Activity log filter').t`Last 12 months` },
    ];

    const clearFilters = () => {
        setSearch('');
        setDebouncedSearch('');
        setSelectedActions([]);
        setOrganizationId('');
        setPeriod('ALL');
    };

    const state = refusal ? (
        // Trois états et non deux : un échec rendu en absence enverrait l'opérateur
        // convaincu que le journal est vide, alors qu'un journal vide est une page
        // vide et non un refus.
        <RegionState
            tone="error"
            title={refusal.title}
            description={
                <>
                    {refusal.guidance}
                    {refusal.detail && (
                        <span className="mt-2 block break-words rounded-md border border-border-norm bg-background-weak p-2 font-mono text-xs">
                            {refusal.detail}
                        </span>
                    )}
                </>
            }
        />
    ) : !showSkeleton && items.length === 0 && filtered ? (
        <RegionState
            title={c('Activity log').t`No event matches these criteria.`}
            description={c('Activity log')
                .t`The search reads the account name and the actor's name or address, and the other filters narrow it further. Widen the period, or clear a filter, to see more.`}
        >
            <Button category="weak" size="small" onClick={clearFilters}>
                {c('Activity log').t`Clear filters`}
            </Button>
        </RegionState>
    ) : !showSkeleton && items.length === 0 && !filtered ? (
        <RegionState
            title={c('Activity log').t`No administration gesture has been logged yet.`}
            description={c('Activity log')
                .t`The audit log fills as accounts are configured, users invited and roles changed. Token issuance and other operational traffic are deliberately left out of it.`}
        />
    ) : null;

    return (
        <section className="flex flex-col gap-5">
            <header className="flex flex-wrap items-start gap-4">
                <div className="min-w-0">
                    <h1 className="text-xl font-semibold text-text-900">{c('Activity log').t`Activity log`}</h1>
                    <p className="mt-1 max-w-2xl text-xs text-text-600">
                        {c('Activity log')
                            .t`Every administration gesture on client accounts, read-only. This is an audit log (ISO 27001): its lines are never edited nor deleted.`}
                    </p>
                </div>
                {/* Le bandeau « Audit · lecture seule » : un rappel, pas un contrôle. Le
                    point reprend le vert de la famille « Utilisateurs ». */}
                <span className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-full border border-border-norm bg-background-norm px-3 py-1.5 text-[11.5px] font-semibold text-text-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--badge-user-color)]" aria-hidden="true" />
                    {c('Activity log').t`Audit · read-only`}
                </span>
            </header>

            <ListCard
                label={c('Activity log').t`Activity log`}
                toolbar={
                    <div className="flex flex-wrap items-center gap-3">
                        <Input
                            value={search}
                            onValue={setSearch}
                            clearable
                            prefix={<MagnifyingGlass size={16} />}
                            placeholder={c('Activity log filter').t`Search an actor, an object, an account…`}
                            aria-label={c('Activity log filter').t`Search the log`}
                            className="max-w-sm"
                        />

                        {/* Options triées par famille (l'ordre du référentiel), donc les familles
                            se suivent dans le menu. Au-delà de douze options, FilterPill affiche
                            sa propre recherche. La pastille compte les types cochés. */}
                        <FilterPill
                            label={c('Activity log filter').t`Action type`}
                            options={actionOptions}
                            values={selectedActions}
                            onValues={setSelectedActions}
                            count={selectedActions.length || undefined}
                            searchLabel={c('Activity log filter').t`Search an action`}
                            noMatchLabel={c('Activity log filter').t`No action matches this search.`}
                        />

                        <FilterPill
                            label={c('Activity log filter').t`Account`}
                            mode="single"
                            options={accountOptions}
                            values={[organizationId]}
                            onValues={([value]) => setOrganizationId(value)}
                            // « Tous les comptes » ne retranche rien : pas de pastille sur ce choix.
                            count={organizationId ? 1 : undefined}
                            searchLabel={c('Activity log filter').t`Search an account`}
                            emptyLabel={c('Activity log filter').t`No account to filter on.`}
                            noMatchLabel={c('Activity log filter').t`No account matches this search.`}
                        />

                        <FilterPill
                            label={c('Activity log filter').t`Period`}
                            mode="single"
                            options={periodOptions}
                            values={[period]}
                            onValues={([value]) => setPeriod(value)}
                            count={period === 'ALL' ? undefined : 1}
                        />

                        {filtered && (
                            <Button className="ml-auto" category="link" size="small" onClick={clearFilters}>
                                {c('Activity log filter').t`Reset`}
                            </Button>
                        )}
                    </div>
                }
                footer={
                    !showSkeleton &&
                    !error &&
                    items.length > 0 && (
                        <div className="px-4">
                            <Pagination
                                page={page}
                                pageSize={PAGE_SIZE}
                                totalCount={total}
                                summary={summary}
                                onPageChange={setPage}
                            />
                        </div>
                    )
                }
            >
                <JournalTable rows={items} loading={showSkeleton} hasError={Boolean(error)} state={state} />
            </ListCard>
        </section>
    );
};

export default JournalPage;
